"use client";

import { Cancel01Icon, Notification03Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useNotificationPrompt } from "@/hooks/use-notification-prompt";
import { cn } from "@/lib/utils";
type NotifyPermissionBannerProps = {
  hasBottomNav?: boolean;
};
export function NotifyPermissionBanner({
  hasBottomNav = false
}: NotifyPermissionBannerProps = {}) {
  const {
    shouldShowNudge,
    requestPermission,
    dismiss
  } = useNotificationPrompt();
  const [busy, setBusy] = useState(false);
  const searchParams = useSearchParams();
  const isTestMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (searchParams?.get("test") === "1") {
      window.sessionStorage.setItem("clutch:test-mode", "1");
      return true;
    }
    return window.sessionStorage.getItem("clutch:test-mode") === "1";
  }, [searchParams]);
  if (isTestMode) return null;
  if (!shouldShowNudge) return null;
  const handlePrimary = async () => {
    setBusy(true);
    try {
      await requestPermission();
    } finally {
      setBusy(false);
    }
  };
  return <section className={cn("fixed right-0 bottom-0 left-0 z-30 print:hidden", hasBottomNav ? "pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-4" : "pb-[calc(env(safe-area-inset-bottom)+1rem)] md:pb-4", "md:right-4 md:bottom-4 md:left-auto md:w-[26rem] md:max-w-[26rem]", "pointer-events-none")} aria-label="Enable notifications">
			<Card size="sm" className={cn("pointer-events-auto mx-3 border border-border/70 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85", "md:mx-0 md:w-full")}>
				<CardHeader className="gap-0">
					<div className="flex items-center gap-3">
						<div className="shrink-0 rounded-2xl border border-border/70 bg-muted/40 p-2">
							<HugeiconsIcon icon={Notification03Icon} size={28} />
						</div>

						<div className="min-w-0 flex-1 space-y-1">
							<CardTitle className="text-base leading-tight">
								Stay updated
							</CardTitle>
							<CardDescription className="text-sm leading-relaxed">
								Get notified about registrations, match updates, and approvals.
							</CardDescription>
						</div>

						<Button type="button" size="icon-sm" variant="ghost" onClick={() => dismiss(false)} aria-label="Dismiss notification prompt" className="shrink-0 self-start">
							<HugeiconsIcon icon={Cancel01Icon} size={18} />
						</Button>
					</div>
				</CardHeader>

				<Separator className="bg-border/70" />

				<CardFooter className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
					<Button size="lg" onClick={handlePrimary} disabled={busy} className="w-full">
						<HugeiconsIcon icon={Notification03Icon} size={16} />
						Turn on notifications
					</Button>
				</CardFooter>
			</Card>
		</section>;
}